import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useLookups } from '../../lib/lookups';
import { api } from '../../lib/api';
import { money, useMutation, useResource } from '../../lib/hooks';

type Test = {
  id: number;
  topic: string;
  dateTime: string;
  totalMarks: number;
  course?: {
    grade?: { description: string };
    subject?: { description: string };
    groupName?: string;
    teacher?: { name: string };
  };
};

type UserSummary = {
  userId: string;
  userName: string;
  email: string;
  role: string;
  isActive: boolean;
};

type InstituteMaster = {
  id: number;
  instituteName: string;
  campusName?: string;
  address?: string;
  phoneNo?: string;
  emailAddress?: string;
};

/**
 * Tests.
 *
 * Marks are not typed in here: the coordinator downloads the generated score sheet, fills
 * the marks column and uploads it back. That round trip is unchanged, so sheets already
 * saved on people's machines still work.
 */
export function TestsPage() {
  const { run, busy } = useMutation();
  const navigate = useNavigate();
  const lookups = useLookups();

  async function download(testId: number) {
    await run(async () => {
      const response = await fetch(`/api/test-scores/${testId}/sheet`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ccms.token')}` },
      });
      if (!response.ok) throw new Error('Could not build the score sheet.');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `score-sheet-T-${testId}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    }, 'Score sheet downloaded.');
  }

  async function upload(testId: number, file: File, reload: () => Promise<void>) {
    const body = new FormData();
    body.append('file', file);

    await run(
      async () => {
        const response = await fetch(`/api/test-scores/${testId}/sheet`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('ccms.token')}` },
          body,
        });

        if (!response.ok) {
          const problem = await response.json().catch(() => null);
          throw new Error(problem?.message ?? 'Upload failed.');
        }
      },
      'Scores updated.',
      reload,
    );
  }

  return (
    <ListPage<Test>
      filters={[
        { key: 'id', label: 'Test ID', type: 'number' },
        { key: 'topic', label: 'Topic', type: 'text', wide: true },
        { key: 'teacherId', label: 'Teacher', type: 'select', options: lookups.teachers },
        { key: 'gradeId', label: 'Grade', type: 'select', options: lookups.grades },
        { key: 'subjectId', label: 'Subject', type: 'select', options: lookups.subjects },
        { key: 'totalMarksFrom', label: 'Marks from', type: 'number' },
        { key: 'totalMarksTo', label: 'Marks to', type: 'number' },
      ]}
      title="Tests"
      actions={
        <button type="button" className="btn btn--accent" onClick={() => navigate('/tests/new')}>
          New test
        </button>
      }
      subtitle="Download a score sheet, fill in the marks, upload it back."
      endpoint="/api/tests"
      rowKey={(r) => r.id}
      emptyMessage="No tests found."
      columns={[
        { key: 'id', header: 'ID', render: (r) => `T-${r.id}`, width: '80px', nowrap: true },
        { key: 'topic', header: 'Topic', render: (r) => r.topic },
        {
          key: 'course',
          header: 'Course',
          render: (r) =>
            [r.course?.grade?.description, r.course?.subject?.description, r.course?.groupName]
              .filter(Boolean)
              .join(' · ') || '—',
        },
        { key: 'teacher', header: 'Teacher', render: (r) => r.course?.teacher?.name ?? '—' },
        {
          key: 'date',
          header: 'Date',
          render: (r) => (r.dateTime ? new Date(r.dateTime).toLocaleDateString() : '—'),
        },
        { key: 'marks', header: 'Total Marks', align: 'right', render: (r) => r.totalMarks },
      ]}
      rowActions={(row, reload) => (
        <>
          <button type="button" className="btn btn--sm" onClick={() => navigate('/tests/' + row.id + '/edit')}>Edit</button>
          <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void download(row.id)}>
            Sheet
          </button>
          <label className="btn btn--sm" style={{ cursor: 'pointer' }}>
            Upload
            <input
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload(row.id, file, reload);
                event.target.value = '';
              }}
            />
          </label>
        </>
      )}
      remove={{
        url: (r) => `/api/tests/${r.id}`,
        successMessage: 'Test removed.',
        describe: (r) => (
          <p>
            Remove test <strong>T-{r.id}</strong> ({r.topic})? Recorded scores are kept.
          </p>
        ),
      }}
    />
  );
}

/**
 * Login accounts.
 *
 * The server enforces the asymmetries a Coordinator is subject to — they cannot create
 * another Coordinator, and cannot reset a Director's or Coordinator's password. The UI does
 * not try to duplicate those rules; it surfaces the server's refusal as a toast.
 */
export function UsersPage() {
  const { run, busy } = useMutation();
  const navigate = useNavigate();
  const [resetting, setResetting] = useState<UserSummary | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  async function resetPassword() {
    if (!resetting) return;

    const ok = await run(
      () =>
        api.post('/api/users/reset-password', {
          userId: resetting.userId,
          newPassword,
          confirmPassword,
        }),
      'Password reset.',
    );

    if (ok) {
      setResetting(null);
      setNewPassword('');
      setConfirmPassword('');
    }
  }

  return (
    <>
      <ListPage<UserSummary>
        filters={[
          { key: 'userName', label: 'Username', type: 'text' },
          { key: 'email', label: 'Email', type: 'text', wide: true },
          {
            key: 'role', label: 'Role', type: 'select',
            options: ['Director', 'Coordinator', 'Cashier', 'Teacher', 'Student']
              .map((r) => ({ value: r, label: r })),
          },
        ]}
        title="Users"
        subtitle="Login accounts. Only Director, Coordinator and Cashier can sign in."
        endpoint="/api/users"
        rowKey={(r) => r.userId}
        emptyMessage="No users found."
        columns={[
          { key: 'username', header: 'Username', render: (r) => r.userName },
          { key: 'email', header: 'Email', render: (r) => r.email },
          { key: 'role', header: 'Role', render: (r) => r.role ?? '—' },
        ]}
        rowActions={(row) => (
          <>
            <button type="button" className="btn btn--sm" onClick={() => navigate('/users/' + row.userId + '/edit')}>Edit</button>
            <button type="button" className="btn btn--sm" onClick={() => setResetting(row)}>Reset password</button>
          </>
        )}
      />

      {resetting && (
        <div className="modal__backdrop" onMouseDown={() => setResetting(null)}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h2 className="modal__title">Reset password for {resetting.userName}</h2>
            <div className="modal__body">
              <div className="field">
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="field">
                <label htmlFor="confirm-password">Confirm password</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn" onClick={() => setResetting(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy || !newPassword || newPassword !== confirmPassword}
                onClick={() => void resetPassword()}
              >
                {busy ? 'Working…' : 'Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Institute details and the registration fee applied to new students. */
export function MasterInformationPage() {
  const institute = useResource<InstituteMaster>('/api/master-information/institute');
  const fees = useResource<{ amount: number }>('/api/master-information/registration-fees');
  const { run, busy } = useMutation();

  const [form, setForm] = useState<InstituteMaster | null>(null);
  const [feeAmount, setFeeAmount] = useState('');

  useEffect(() => {
    if (institute.data) setForm(institute.data);
  }, [institute.data]);

  useEffect(() => {
    if (fees.data) setFeeAmount(String(fees.data.amount));
  }, [fees.data]);

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Master Information</h2>
          <p className="page__subtitle">Institute details and the default registration fee.</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <h3 className="card__title">Institute</h3>

        {institute.loading && <p>Loading…</p>}
        {institute.error && <p className="field__error">{institute.error}</p>}

        {form && (
          <>
            <div className="field">
              <label htmlFor="institute-name">Institute name</label>
              <input
                id="institute-name"
                value={form.instituteName ?? ''}
                onChange={(e) => setForm({ ...form, instituteName: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="campus-name">Campus name</label>
              <input
                id="campus-name"
                value={form.campusName ?? ''}
                onChange={(e) => setForm({ ...form, campusName: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="institute-address">Address</label>
              <input
                id="institute-address"
                value={form.address ?? ''}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="institute-phone">Phone</label>
              <input
                id="institute-phone"
                value={form.phoneNo ?? ''}
                onChange={(e) => setForm({ ...form, phoneNo: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="institute-email">Email</label>
              <input
                id="institute-email"
                value={form.emailAddress ?? ''}
                onChange={(e) => setForm({ ...form, emailAddress: e.target.value })}
              />
            </div>

            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() =>
                void run(
                  () => api.put('/api/master-information/institute', form),
                  'Institute information saved.',
                  () => institute.reload(),
                )
              }
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        )}
      </div>

      <div className="card">
        <h3 className="card__title">Registration fee</h3>
        <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
          Applies to newly registered students only. Existing registration fees keep the amount they
          were created with. Current: <strong className="money">{money(fees.data?.amount)}</strong>
        </p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div className="field" style={{ margin: 0, minWidth: 180 }}>
            <label htmlFor="registration-fee">New amount</label>
            <input
              id="registration-fee"
              value={feeAmount}
              onChange={(e) => setFeeAmount(e.target.value)}
              inputMode="decimal"
            />
          </div>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || !(Number(feeAmount) > 0)}
            onClick={() =>
              void run(
                () => api.put('/api/master-information/registration-fees', { amount: Number(feeAmount) }),
                'Registration fee updated.',
                () => fees.reload(),
              )
            }
          >
            {busy ? 'Saving…' : 'Update'}
          </button>
        </div>
      </div>
    </>
  );
}
